import {ChangeDetectionStrategy, Component, computed, input, model, output} from '@angular/core';
import {TenderSelection} from '@frontend/shared-api';
import {TranslocoPipe} from '@jsverse/transloco';
import {ColorLegendComponent} from '../../../../../../../shared/components/color-legend/color-legend.component';
import {ColorLegendEntry} from '../../../../../../../shared/components/color-legend/color-legend.types';
import {ModelFieldPercentageComponent} from '../../../../../../../shared/components/inputs/model-field/model-field-percentage/model-field-percentage.component';
import {ModelFieldSelectButtonComponent} from '../../../../../../../shared/components/inputs/model-field/model-field-select-button/model-field-select-button.component';
import {
  RfpParticipatingTool,
  SuitabilityMode,
  SuitabilityThresholds,
} from '../rfp-invitation.types';
import {isPreferred} from '../rfp-suitability.calc';
import {
  COUNTRY_BAND_COLORS,
  RfpSuitabilityMatrixComponent,
} from '../rfp-suitability-matrix/rfp-suitability-matrix.component';
import {RfpSelectedToolCardComponent} from './rfp-selected-tool-card/rfp-selected-tool-card.component';
import {RfpSelectionChange} from './rfp-suitability-panel.types';

const KEY = 'project.steps.rfp.invitationList.matrix.';

@Component({
  selector: 'app-rfp-suitability-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoPipe,
    ColorLegendComponent,
    ModelFieldPercentageComponent,
    ModelFieldSelectButtonComponent,
    RfpSuitabilityMatrixComponent,
    RfpSelectedToolCardComponent,
  ],
  templateUrl: './rfp-suitability-panel.component.html',
})
export class RfpSuitabilityPanelComponent {
  protected readonly modeOptions = [
    {value: 'combined', labelKey: `${KEY}modeCombined`},
    {value: 'functions', labelKey: `${KEY}modeFunctions`},
  ];
  protected readonly legendEntries: ColorLegendEntry[] = [
    {color: COUNTRY_BAND_COLORS.high, labelKey: `${KEY}countriesHigh`},
    {color: COUNTRY_BAND_COLORS.medium, labelKey: `${KEY}countriesMedium`},
    {color: COUNTRY_BAND_COLORS.low, labelKey: `${KEY}countriesLow`},
  ];

  readonly tools = input.required<RfpParticipatingTool[]>();
  readonly locked = input(false);

  readonly mode = model.required<SuitabilityMode>();
  readonly thresholds = model.required<SuitabilityThresholds>();
  readonly selectedToolId = model<string | null>(null);

  readonly selectionChange = output<RfpSelectionChange>();

  protected readonly selectedTool = computed(() => {
    const tools = this.tools();
    return tools.find((tool) => tool.id === this.selectedToolId()) ?? tools.at(0) ?? null;
  });
  protected readonly matrixSelectedToolId = computed(() => this.selectedTool()?.id ?? null);
  protected readonly selectedToolPreferred = computed(() => {
    const tool = this.selectedTool();
    return tool ? isPreferred(tool, this.mode(), this.thresholds()) : false;
  });
  protected readonly coverageThreshold = computed(() => this.thresholds().coverage);
  protected readonly rfiThreshold = computed(() => this.thresholds().rfi);
  protected readonly summaryParams = computed(() => ({
    participants: this.tools().filter((tool) => tool.selection === TenderSelection.PARTICIPANT)
      .length,
    preferred: this.tools().filter((tool) => isPreferred(tool, this.mode(), this.thresholds()))
      .length,
  }));

  protected setCoverageThreshold(value: number | null): void {
    this.thresholds.update((current) => ({...current, coverage: this.clampPercent(value)}));
  }

  protected setRfiThreshold(value: number | null): void {
    this.thresholds.update((current) => ({...current, rfi: this.clampPercent(value)}));
  }

  private clampPercent(value: number | null): number {
    return Math.min(100, Math.max(0, value ?? 0));
  }
}
